import { List, Map } from 'immutable';

import * as Router from '@/domain/router/RouterActions';
import * as Companies from '@/domain/companies/companiesActions';
import * as Documents from '@/domain/documents/documentsActions';
import * as Categories from '@/domain/categories/actions';
import * as Textract from '@/domain/textract/actions';
import * as Payments from '@/domain/payments/actions';
import * as PaySettings from '@/domain/paySettings/actions';
import * as PayCategories from '@/domain/payCategories/actions';
import { permissionsChangedAction } from '@/domain/ws/global/events/permissionsChanged';
import * as adapters from '@/domain/env/adapters';
import { EnvFactory, roleModificatorsFactory } from '@/domain/env/factories';
import * as A from '@/domain/env/envActions';

import { isMobile } from '@/lib/systemHelpers/browserHelpers';

export const reducer = (initial) => ({
  env(state = adapters.envAdapter(initial), action) {
    switch (action.type) {
      case A.signInAction.success: {
        const { limited, restricted, readonly } = action.payload;
        return (
          state
            .set('newChatFlow', action.payload.newChatFlow)
            .set('dokkaToken', action.payload.dokkaToken)
            .set('role', action.payload.role)
            .set('chatToken', action.payload.chatToken)
            .set('userId', action.payload.userID)
            .set('superUser', action.payload.isSuperUser)
            .set('isOrgAdmin', Boolean(action.payload.isOrgAdmin))
            .set('organizationId', action.payload.organizationId)
            .set('organizationName', action.payload.organizationName)
            .set('organizationBot', adapters.orgBotAdapter(action.payload.organizationBot || {}))
            .set('features', adapters.featuresAdapter(action.payload.features || {}))
            .set('firstName', action.payload.firstName)
            .set('lastName', action.payload.lastName)
            .set('picture', action.payload.picture)
            .set('phone', action.payload.phone)
            .set('title', action.payload.title)
            .set('chatUser', action.payload.displayName)
            .set('userGUID', action.payload.userGUID)
            .set('permissions', adapters.permissionsAdapter({ ...action.payload.permissions }))
            .set('isDokkaSupport', action.payload.isDokkaSupport)
            .set('helpdeskLink', action.payload.support_portal)
            .set('allowFreshdesk', action.payload.allowFreshdesk)
            .set('roleModificators', adapters.roleModificatorsAdapter({ limited, restricted, readonly }))
            .set('companyPermissions', adapters.companyPermissionsAdapter(action.payload.companyPermissions))
            .set('cognitoUsername', action.payload.cognitoUsername)
            .set('beta', action.payload.beta)
            .set('apps', action.payload.apps)
            // Shared per-user recording flag; gates the Fullstory recorder.
            // TEMPORARY legacy fallback for phased rollout: prefer
            // `isRecordingEnabled`, falling back to the legacy `isFullstoryRecording`
            // field so FE/BE deploy-order lag can't silently disable recording.
            // Coerced to a strict boolean. Remove the fallback once the backend
            // ships `isRecordingEnabled` everywhere.
            .set(
              'isRecordingEnabled',
              (action.payload.isRecordingEnabled ?? action.payload.isFullstoryRecording) === true,
            )
        );
      }

      // Entering another company drops the previous company's pay permissions, so the next DOKKA Pay page refetches
      // them instead of scoping them against the new company's providers (DPM-846).
      case Companies.companySignInAction.success:
        return state
          .set('payModuleCompanyPermissions', List())
          .set('isPayModuleCompanyPermissionsLoaded', false)
          .set('loading', state.loading > 0 ? state.loading - 1 : 0);

      case A.getPayModuleCompanyPermissionsAction.success:
        return state
          .set('payModuleCompanyPermissions', action.payload)
          .set('isPayModuleCompanyPermissionsLoaded', true)
          .set('loading', state.loading > 0 ? state.loading - 1 : 0);

      case A.expiredAction.type:
        return state.set('isExpired', true);

      case permissionsChangedAction.type:
        return state.set('isPermissionsChanged', true);

      case A.cognitoCleanAuthDataAction.type:
      case A.signOutAction.success:
        return EnvFactory({
          locale: state.locale,
          rtl: state.rtl,
          linkedSidebarWidth: state.linkedSidebarWidth,
          role: null,
          roleModificators: roleModificatorsFactory(),
          connectionHealth: state.connectionHealth,
        });

      case A.signInWithOrganizationAction.type:
        return state.set('dokkaToken', action.payload.dokkaToken);

      case A.setOrganizationIdAction.type:
        return state.set('organizationId', action.payload.organizationId);

      case A.setOrgAdminAction.type:
        return state.set('isOrgAdmin', action.payload.isOrgAdmin);

      case A.documentChangeLinkedWidthAction.type:
        return state.set('linkedSidebarWidth', action.payload);

      case A.paymentUpdateViewAction.type:
        return state.set('paymentView', new Map(action.payload));

      case A.updateSupplierAliasesAction.success:
        return state.set('supplierAliases', action.payload);

      case A.setSagaDataLoadingAction.type:
        return state.set('isSagaDataLoading', action.payload);

      case A.signInWithOrganizationAction.request:
      case Documents.documentAccept.request:
      case Documents.documentSearchAction.request:
      case Documents.supplierDocumentSearchAction.request:
      case Companies.fetchCompanies.request:
      case Companies.companySignInAction.request:
      case Documents.scanningRequest.type:
      case Documents.documentGet.request:
      case Documents.documentStopProcessingAction.request:
      case Documents.permanentlyDeleteAllDocumentsAction.type:
      case Categories.getCategoriesAction.request:
      case Documents.mergeDocumentsAction.type:
      case Documents.documentSplit.type:
      case Documents.documentGetLinkedIDsAction.type:
      case Documents.createDocumentPaymentAction.type:
      case A.locationChangeAction.type:
      case A.addDataLoadingAction.type:
      case Companies.setSearchFilterAction.request:
      case Textract.ExtractLinesAction.type:
      case A.getPayModuleCompanyPermissionsAction.request:
      case PaySettings.getProvidersAction.request:
      case PaySettings.connectProviderAction.type:
      case PaySettings.disconnectProviderAction.type:
      case PaySettings.requestAccessToProviderAction.type:
      case PaySettings.getProviderApproversAction.request:
      case Payments.paymentGetAction.request:
      case Payments.paymentUpdateInfoAction.type:
      case Payments.paymentUpdateInvoicesAction.type:
      case Payments.startPaymentApprovalFlowAction.type:
      case Payments.voidPaymentAction.type:
      case Payments.recreatePaymentAction.type:
      case Payments.resetPaymentProcessAction.type:
      case Payments.approvePaymentAction.type:
      case Payments.rejectPaymentAction.type:
      case Payments.payPaymentAction.type:
      case Payments.updatePaymentTagsAction.type:
      case Payments.unmergePaymentAction.type:
      case Payments.paymentsBulkMergeAction.type:
      case Payments.paymentsBulkSendToApprovalAction.type:
      case Payments.paymentsBulkSaveForLaterAction.type:
      case Payments.paymentsBulkArchiveAction.type:
      case Payments.paymentsBulkApproveAction.type:
      case Payments.paymentsBulkResetApprovalFlowAction.type:
      case Payments.paymentsBulkPayAction.type:
      case Payments.paymentsBulkDeleteAction.type:
      case Payments.deletePaymentAction.type:
      case PayCategories.getPayCategoriesAction.type:
        return state.set('loading', state.loading + 1);

      case Documents.documentAccept.success:
      case A.signInWithOrganizationAction.failure:
      case A.removeDataLoadingAction.type:
      case A.signInWithOrganizationAction.success:
      case Documents.documentAccept.failure:
      case Documents.documentUpdate.success:
      case Documents.documentUpdate.failure:
      case Documents.documentSearchAction.success:
      case Documents.documentSearchAction.failure:
      case Documents.supplierDocumentSearchAction.success:
      case Documents.supplierDocumentSearchAction.failure:
      case Documents.supplierDocumentRefreshAction.success:
      case Documents.supplierDocumentRefreshAction.failure:
      case Documents.documentForceSearch.success:
      case Documents.documentForceSearch.failure:
      case Documents.documentWorkerSearch.success:
      case Documents.documentWorkerSearch.failure:
      case Documents.scanningRequest.success:
      case Documents.scanningRequest.failure:
      case Companies.fetchCompanies.success:
      case Companies.fetchCompanies.failure:
      case Companies.companySignInAction.failure:
      case Documents.nextDocumentAction.failure:
      case Documents.documentStopProcessingAction.success:
      case Documents.documentStopProcessingAction.failure:
      case Documents.permanentlyDeleteAllDocumentsAction.success:
      case Documents.permanentlyDeleteAllDocumentsAction.failure:
      case Categories.getCategoriesAction.success:
      case Categories.getCategoriesAction.failure:
      case Documents.mergeDocumentsAction.success:
      case Documents.mergeDocumentsAction.failure:
      case Documents.documentGetLinkedIDsAction.success:
      case Documents.documentGetLinkedIDsAction.failure:
      case Documents.createDocumentPaymentAction.success:
      case Documents.createDocumentPaymentAction.failure:
      case Companies.setSearchFilterAction.success:
      case Companies.setSearchFilterAction.failure:
      case Textract.ExtractLinesAction.success:
      case Textract.ExtractLinesAction.failure:
      case Documents.mergeDocumentsForPrintingAction.failure:
      case A.getPayModuleCompanyPermissionsAction.failure:
      case PaySettings.getProvidersAction.success:
      case PaySettings.getProvidersAction.failure:
      case PaySettings.connectProviderAction.success:
      case PaySettings.connectProviderAction.failure:
      case PaySettings.disconnectProviderAction.success:
      case PaySettings.disconnectProviderAction.failure:
      case PaySettings.requestAccessToProviderAction.success:
      case PaySettings.requestAccessToProviderAction.failure:
      case PaySettings.getProviderApproversAction.success:
      case PaySettings.getProviderApproversAction.failure:
      case Payments.paymentGetAction.success:
      case Payments.paymentGetAction.failure:
      case Payments.paymentUpdateInfoAction.success:
      case Payments.paymentUpdateInfoAction.failure:
      case Payments.paymentUpdateInvoicesAction.success:
      case Payments.paymentUpdateInvoicesAction.failure:
      case Payments.startPaymentApprovalFlowAction.success:
      case Payments.startPaymentApprovalFlowAction.failure:
      case Payments.voidPaymentAction.success:
      case Payments.voidPaymentAction.failure:
      case Payments.recreatePaymentAction.success:
      case Payments.recreatePaymentAction.failure:
      case Payments.resetPaymentProcessAction.success:
      case Payments.resetPaymentProcessAction.failure:
      case Payments.approvePaymentAction.success:
      case Payments.approvePaymentAction.failure:
      case Payments.rejectPaymentAction.success:
      case Payments.rejectPaymentAction.failure:
      case Payments.payPaymentAction.success:
      case Payments.payPaymentAction.failure:
      case Payments.updatePaymentTagsAction.success:
      case Payments.updatePaymentTagsAction.failure:
      case Payments.unmergePaymentAction.success:
      case Payments.unmergePaymentAction.failure:
      case Payments.paymentsBulkMergeAction.success:
      case Payments.paymentsBulkMergeAction.failure:
      case Payments.paymentsBulkSendToApprovalAction.success:
      case Payments.paymentsBulkSendToApprovalAction.failure:
      case Payments.paymentsBulkSaveForLaterAction.success:
      case Payments.paymentsBulkSaveForLaterAction.failure:
      case Payments.paymentsBulkArchiveAction.success:
      case Payments.paymentsBulkArchiveAction.failure:
      case Payments.paymentsBulkApproveAction.success:
      case Payments.paymentsBulkApproveAction.failure:
      case Payments.paymentsBulkResetApprovalFlowAction.success:
      case Payments.paymentsBulkResetApprovalFlowAction.failure:
      case Payments.paymentsBulkPayAction.success:
      case Payments.paymentsBulkPayAction.failure:
      case Payments.paymentsBulkDeleteAction.success:
      case Payments.paymentsBulkDeleteAction.failure:
      case Payments.deletePaymentAction.success:
      case Payments.deletePaymentAction.failure:
      case PayCategories.getPayCategoriesAction.success:
      case PayCategories.getPayCategoriesAction.failure:
        return state.set('loading', state.loading > 0 ? state.loading - 1 : 0);

      case Documents.documentGet.success:
      case Documents.documentGet.failure:
        return isMobile ? state.set('loading', state.loading > 0 ? state.loading - 1 : 0) : state;

      case Router.completeAction.type:
      case Router.completeResetAction.type:
      case Router.noLocationChange.type:
        return state.set('loading', 0);

      case A.setNavigationAction.type:
        return state.set('navigation', Map(action.payload));

      case A.updateNavigationAction.type:
        return state.update('navigation', (n) => n.merge(action.payload));

      case A.clearNavigationAction.type:
        return state.set('navigation', new Map());

      case A.saveBackSearchUrlAction.type:
        return state.set('backSearchUrl', action.payload);

      case A.savePersistedBackUrlAction.type:
        return state.set('persistedBackUrl', action.payload);

      case A.setGoogleSearchTextAction.type:
        return state.set('googleSearchText', action.payload).set('googlePanelStatus', true);

      case A.setGooglePanelStatusAction.type:
        return state.set('googlePanelStatus', action.payload);

      case A.addAppStatusAction.type:
        return state.update('appStatuses', (appStatuses) =>
          appStatuses.filter((s) => s.id !== action.payload.id).push(action.payload),
        );

      case A.removeAppStatusAction.type:
        return state.update('appStatuses', (appStatuses) => appStatuses.filter((s) => s.id !== action.payload));

      case A.userPictureUpdatedAction.type:
        return state.set('picture', action.payload);

      case A.userProfileUpdatedAction.type:
        return state
          .set('firstName', action.payload.first_name)
          .set('lastName', action.payload.last_name)
          .set('phone', action.payload.phone)
          .set('title', action.payload.title)
          .set('chatUser', [action.payload.first_name, action.payload.last_name].join(' '));

      case A.updateWorkSpaceAction.type:
        return state.setIn(['workSpace', action.payload.companyId], action.payload.type);

      case A.updateUserFeaturesAction.success:
        return state.set('features', action.payload);

      case A.setErrorMessage.type: {
        return state.set('error', action.payload);
      }

      case A.connectionHealthAction.type:
        return state.set('connectionHealth', action.payload);

      default:
        return state;
    }
  },
});
